import { z } from 'zod';
import { ConsentDocumentSchema } from './notifications.js';
/**
 * `GET /api/v1/public/tenant` (API.md §2.3): everything the patient app needs to
 * render itself before anyone signs in. Unauthenticated, so it carries no PHI and no
 * tenant id - the host already identifies the tenant, and echoing the uuid would only
 * help someone enumerate them.
 */
/** Which app the resolved hostname serves (DATABASE.md §7.1 `tenant_domains.app_kind`). */
export const AppKindSchema = z.enum(['patient', 'admin', 'super']);
/** Visual identity from `tenant_branding`. Every field is optional by design. */
export const TenantBrandingSchema = z.object({
    clinicName: z.string(),
    /**
     * Where the clinic's mark is served from, or `null` when it has not uploaded one.
     *
     * A **stable path** (`/api/v1/public/branding/logo`), not a signed URL. The signature
     * behind it lives minutes and this payload is cached for sixty seconds by every
     * browser that has it, so a signed URL here would be an `<img>` that works for the
     * first visitor and 403s for the next. The route redirects to a freshly signed URL on
     * every request and caches *that* for five minutes (API.md §2.3); the `?v=` suffix
     * (below) is what makes a changed logo a different URL.
     */
    logoUrl: z.string().nullable(),
    faviconUrl: z.string().nullable(),
    /**
     * M17.1: `tenant_branding.updated_at` in epoch seconds (0 when the clinic has no
     * branding row). `logoUrl` and `faviconUrl` carry it as `?v=<brandVersion>`, so a new
     * upload is a new URL and the five-minute cache on the redirect cannot serve the old
     * mark. The branding routes ignore the query; it exists only to change the URL.
     */
    brandVersion: z.int().min(0),
    primaryColor: z.string().nullable(),
    /**
     * M11.12: the states a design system needs from one brand colour - hover, a subtle
     * tint and a divider. Added to the public payload rather than derived in the client,
     * so the patient app and the admin app paint the same button.
     */
    primaryHoverColor: z.string().nullable(),
    primarySubtleColor: z.string().nullable(),
    borderColor: z.string().nullable(),
    secondaryColor: z.string().nullable(),
    accentColor: z.string().nullable(),
    fontFamily: z.string().nullable(),
    supportEmail: z.string().nullable(),
    supportPhone: z.string().nullable(),
});
/**
 * One consent the funnel must show, with the version it is showing.
 *
 * The version is the point. A `consents` row records what the patient accepted and
 * when (DATABASE.md §7.4), and a row whose `document_version` does not name a text we
 * can still produce is not a consent anybody can defend two years later. So the text
 * and its version travel together, from the tenant's settings to the funnel to the
 * consent row, and the funnel never invents either.
 *
 * `text` is null when the tenant has not written one: the clinic's own wording is
 * expected, and a platform default would be us writing legal copy on their behalf.
 */
export const TenantConsentTextSchema = z.object({
    kind: z.enum(['telehealth', 'hipaa_notice', 'financial', 'unsecured_email', 'sms', 'marketing']),
    version: z.string(),
    text: z.string().nullable(),
    /** Whether the funnel may proceed without it ticked. */
    required: z.boolean(),
});
export const TenantLegalSchema = z.object({
    privacyUrl: z.string().nullable(),
    termsUrl: z.string().nullable(),
    /** The clinic's financial / cancellation policy, shown before a card is taken. */
    financialPolicyUrl: z.string().nullable(),
    /**
     * Consent texts and their versions, in the order the funnel shows them. Empty until
     * the clinic writes them (M7); the funnel renders what is here and nothing else.
     */
    consents: z.array(TenantConsentTextSchema),
});
/**
 * One bookable visit type, as the *patient* may see it (M5.1).
 *
 * Deliberately smaller than the admin `AppointmentTypeSchema` in `scheduling.ts`:
 * buffers, lead times, EHR reason ids, provider lists and sort orders are operational
 * detail that tells an outsider how the clinic is staffed. What the funnel needs is a
 * name, a description, how long it takes and what it costs.
 */
export const PublicAppointmentTypeSchema = z.object({
    id: z.string(),
    /** The stable handle a marketing link carries: `?type=new-patient`. */
    key: z.string(),
    name: z.string(),
    /** Patient-facing copy. Empty string when the clinic has not written any. */
    description: z.string(),
    durationMinutes: z.number().int().positive(),
    selfPayPriceCents: z.number().int().min(0),
    currency: z.string().length(3),
    /** Whether the funnel shows the insurance step for this type. */
    requiresInsuranceStep: z.boolean(),
});
/**
 * What this clinic has switched on (FR-PLAT-03, ARCHITECTURE.md §17).
 *
 * Two lists, because they answer two different questions and must not be confused:
 *
 * - `keys` are **capability** flags from `plan_features` / `tenant_features` -
 *   `waitlist`, `ai_estimate`, `sms`. They gate behaviour the server also enforces;
 *   the funnel reads them only to decide what to render.
 * - `ui` are **presentation** flags, stored as `tenant_features` rows under the `ui.`
 *   prefix and returned with that prefix stripped, so `ui.funnel.fewerSteps` arrives
 *   as `funnel.fewerSteps`. They change layout and copy and nothing else.
 *
 * Neither list is ever an authorization decision (rules §44.3): a feature that is off
 * answers `FEATURE_DISABLED` from the endpoint that owns it, and a missing permission
 * answers `FORBIDDEN`. A client that lies about either gets the same refusal.
 */
export const TenantFeaturesSchema = z.object({
    keys: z.array(z.string()),
    ui: z.array(z.string()),
    /** Whether `?flags=` on the URL may override `ui` for this tenant. Default false. */
    flagOverrides: z.boolean(),
});
/** The days the funnel may offer, before any per-type narrowing (SCHEDULING_UI.md §5.4). */
export const TenantBookingWindowSchema = z.object({
    minLeadDays: z.number().int().min(0),
    maxHorizonDays: z.number().int().min(1),
    /** Minutes a slot is held during checkout, so the funnel can show a countdown. */
    reservationMinutes: z.number().int().min(1),
});
export const PublicTenantSchema = z.object({
    /** Clinic slug. Already visible in the hostname; useful as a stable cache key. */
    slug: z.string(),
    name: z.string(),
    /** IANA zone the clinic's calendar is expressed in. */
    timeZone: z.string(),
    /** Two-letter states the clinic serves; drives the patient-facing state picker. */
    statesServed: z.array(z.string()),
});
export const PublicTenantResponseSchema = z.object({
    data: z.object({
        tenant: PublicTenantSchema,
        appKind: AppKindSchema,
        branding: TenantBrandingSchema,
        /** Active types only, in the clinic's own order. Inactive ones are simply absent. */
        appointmentTypes: z.array(PublicAppointmentTypeSchema),
        features: TenantFeaturesSchema,
        legal: TenantLegalSchema,
        /**
         * The same consent wording the portal reads from
         * `GET /patient/consents/documents`, keyed by the **`consents` row's** spelling
         * (`marketing_authorization`, not the settings block's `marketing`).
         *
         * `legal.consents` is not going away and is not a duplicate of this: it is the
         * funnel's own list, in the clinic's display order, spelled the way the draft
         * spells it. This is the *document* list, so one component can render a consent
         * sentence on the funnel and in the portal without a translation table in the
         * client - the translation happens once, on the server.
         */
        consentDocuments: z.array(ConsentDocumentSchema),
        /**
         * The states the clinic serves, as the state gate reads them (FR-BOOK-03). The
         * same list as `tenant.statesServed`, hoisted because it is the first question the
         * funnel asks and every client was reaching two levels down for it.
         */
        states: z.array(z.string()),
        bookingWindow: TenantBookingWindowSchema,
    }),
});
//# sourceMappingURL=tenant.js.map